import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { fetchVictims, fetchVictimsByKiller } from "../../api/victims";
import type { VictimDTO } from "@shared/contracts";
import type { Filter } from "../../context/VictimsAndKillersContext";
import { STALE_TIME } from "../../utils/constants";

export function useVictims(filters: Filter[]) {
  const queryDefs = useMemo(
    () =>
      filters.map((r) => {
        if (r.type === "serialkiller" && r.killerId) {
          return {
            queryKey: ["victimsByKiller", r.killerId],
            enabled: !!r.killerId,
            staleTime: STALE_TIME,
            queryFn: ({ signal }: { signal?: AbortSignal }) =>
              fetchVictimsByKiller(r.killerId!, signal),
          };
        }
        const canFetch = !!r.country && !!r.from && !!r.to;
        return {
          queryKey: ["victimsQuery", r.type, r.country, r.region, r.from, r.to, r.id],
          enabled: r.type !== "serialkiller" && canFetch,
          staleTime: STALE_TIME,
          queryFn: ({ signal }: { signal?: AbortSignal }) =>
            fetchVictims(
              {
                status: r.type,
                country: r.country,
                region: r.region,
                from: r.from,
                to: r.to,
              },
              signal
            ),
        };
      }),
    [filters]
  );

  const queries = useQueries({ queries: queryDefs });

  const victimsByFilter = useMemo(
    () =>
      filters.reduce<Record<number, VictimDTO[]>>((acc, r, i) => {
        acc[r.id] = (queries[i]?.data as VictimDTO[]) ?? [];
        return acc;
      }, {}),
    [filters, queries]
  );

  const loadingByFilter = useMemo(
    () =>
      filters.reduce<Record<number, boolean>>((acc, r, i) => {
        acc[r.id] = !!queries[i]?.isLoading;
        return acc;
      }, {}),
    [filters, queries]
  );

  return { victimsByFilter, loadingByFilter };
}